import clsx from 'clsx';
import { ModalPopup, Stack } from 'components';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ScreenerTypeUppercase, ScreenerTypeLowercase } from 'store/screeener/screenerSlice';
import city from 'assets/png/city.png';
import { useParams } from 'react-router-dom';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreenerDetails,
  getScreenerHistory,
  getScreeners
} from 'store/screeener/screenerActions';
import { getMpi } from 'store/clients/clientsActions';

import style from './screener.module.scss';
import { Impact } from './impact';
import { Other } from './other';

const screenerMapper: { [key in ScreenerTypeUppercase]: string } = {
  IMPACT: 'IMPaCT',
  OTHER: 'Other'
};

type ScreenerProps = {
  clientId?: string;
};

type ScreenerModalProps = {
  clientId?: string;
  container?: HTMLElement;
  open: boolean;
  selectedScreener: ScreenerTypeLowercase;
  onSelectScreener: (screener: ScreenerTypeLowercase) => void;
  onClose: () => void;
};

export const ScreenerModal = ({
  clientId,
  container,
  open,
  selectedScreener,
  onSelectScreener,
  onClose
}: ScreenerModalProps) => {
  return (
    <ModalPopup open={open} container={container} customClassInsideContainer={style.modalWrapper}>
      <Stack display="flex" flexDirection="row" height="100%" width="100%">
        <Stack className={style.sidebar} height="100%" display="flex" justifyContent="end">
          <img src={city}></img>
        </Stack>
        <Stack p="24px" width="100%">
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <h3 className={style.modalTitle}>Screener</h3>
            <Stack display="flex" flexDirection="row" alignItems="center" gap="12px">
              <div className={style.roadmapToggle}>
                <div
                  onClick={() => {
                    onSelectScreener('impact');
                  }}
                  className={clsx(
                    style.toggleItem,
                    selectedScreener === 'impact' && style.activeToggle
                  )}
                >
                  <span>IMPaCT</span>
                </div>
                <div
                  onClick={() => {
                    onSelectScreener('other');
                  }}
                  className={clsx(
                    style.toggleItem,
                    selectedScreener === 'other' && style.activeToggle
                  )}
                >
                  <span>Other</span>
                </div>
              </div>
              <div className={style.closeIcon} onClick={onClose}>
                <span className="material-icons">close</span>
              </div>
            </Stack>
          </Stack>
          <Stack height="100%" minHeight={0} pt="36px">
            {selectedScreener === 'impact' && <Impact clientId={clientId} closeModal={onClose} />}
            {selectedScreener === 'other' && <Other clientId={clientId} closeModal={onClose} />}
          </Stack>
        </Stack>
      </Stack>
    </ModalPopup>
  );
};

const Screener = ({ clientId }: ScreenerProps) => {
  const { clientId: routeClientId } = useParams();
  const resolvedClientId = clientId ?? routeClientId ?? '';
  const [showSection, setShowSection] = useState(true);
  const dispatch = useAppDispatch();
  const [selectedScreener, setSelectedScreener] = useState<ScreenerTypeLowercase | null>(null);
  const screeners = useAppSelector((state) => state.screener[resolvedClientId]?.screeners?.data);
  const areAllScreenersPopulated = screeners?.length === 2;

  const onSelectScreener = (screener: ScreenerTypeLowercase) => {
    setSelectedScreener(screener);
  };

  const onClearScreener = () => {
    setSelectedScreener(null);
  };

  const handleToggleSection = () => setShowSection(!showSection);

  useEffect(() => {
    const channel = new BroadcastChannel('screener-update');
    channel.onmessage = async (event) => {
      if (event.data.message === 'SCREENER_SUBMITTED') {
        dispatch(getMpi({ clientId: resolvedClientId }));
        dispatch(getScreeners({ clientId: resolvedClientId }));
        if (event.data.type === 'impact') {
          dispatch(getScreenerDetails({ clientId: resolvedClientId, type: 'impact' }));
          dispatch(getScreenerHistory({ clientId: resolvedClientId, type: 'impact' }));
        }
        if (event.data.type === 'other') {
          dispatch(getOtherScreenerDetails({ clientId: resolvedClientId }));
          dispatch(getOtherScreenerHistory({ clientId: resolvedClientId }));
        }
      }
    };

    return () => {
      channel.close();
    };
  }, [dispatch, resolvedClientId]);

  return (
    <Stack width="100%" data-test-id="profile-snapshot">
      <div className={style.wrapper}>
        <p className={style.title}>Screener</p>
        <div>
          {!areAllScreenersPopulated && (
            <span
              onClick={() => {
                if (screeners?.find((screener) => screener.type === 'IMPACT')) {
                  onSelectScreener('other');
                } else {
                  onSelectScreener('impact');
                }
              }}
              className={clsx('material-icons-outlined', style.icon)}
              data-test-id="add-screener-icon"
            >
              open_in_new
            </span>
          )}
          <span
            onClick={handleToggleSection}
            className={clsx('material-icons-outlined', style.icon)}
          >
            {showSection ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          </span>
        </div>
      </div>
      {!showSection ||
        (screeners?.length > 0 && (
          <Stack display="flex" flexDirection="column" gap="8px" p="12px">
            {screeners?.map((screener) => (
              <button
                key={screener.type}
                onClick={() =>
                  onSelectScreener(screener.type.toLowerCase() as ScreenerTypeLowercase)
                }
                className={style.screener}
              >
                {screenerMapper[screener.type]}
              </button>
            ))}
          </Stack>
        ))}
      {selectedScreener && (
        <ScreenerModal
          clientId={resolvedClientId}
          open={selectedScreener !== null}
          selectedScreener={selectedScreener}
          onSelectScreener={setSelectedScreener}
          onClose={onClearScreener}
        />
      )}
    </Stack>
  );
};

export default Screener;
